export const CHART_TYPES = ["number", "bar", "line", "area", "pie", "table", "combo"] as const;
export type ChartType = typeof CHART_TYPES[number];

export const CHART_TYPE_LABELS: Record<ChartType, string> = {
	number: "Nyckeltal",
	bar: "Stapeldiagram",
	line: "Linjediagram",
	area: "Ytdiagram",
	pie: "Cirkeldiagram",
	table: "Tabell",
	combo: "Stapel + linje",
};

/** What the backend generates from a prompt: SQL against the lake + how to draw it. */
export interface WidgetSpec {
	title: string;
	description?: string;
	/** One SELECT with :from/:to or :from_month/:to_month placeholders. */
	sql: string;
	chart: {
		type: ChartType;
		xKey?: string;
		yKeys?: string[];
		unit?: string;
		stacked?: boolean;
		/** combo: the yKeys drawn as lines; the rest are bars. Defaults to the last yKey. */
		lineKeys?: string[];
		/** combo: unit of the lines when it differs from `unit`; gives the lines their own right-hand axis. */
		lineUnit?: string;
	};
	usesDateRange: boolean;
	/** True when the SQL has a :bucket(col) placeholder, so the grain menu applies. */
	usesBucket?: boolean;
	/** True when the SQL has :filter(col) placeholders, so the report filters apply. */
	usesFilters?: boolean;
	/** Time-axis grain; absent or "auto" = from the effective range length. */
	bucket?: BucketSetting;
	/** Pinned period; absent = follow the report's date range. */
	period?: WidgetPeriod;
}

export type WidgetPeriod = Exclude<DateRangePreset, "custom">;

export const GRAINS = ["day", "week", "month", "quarter", "year"] as const;
export type Grain = typeof GRAINS[number];
export type BucketSetting = Grain | "auto";

export const GRAIN_LABELS: Record<BucketSetting, string> = {
	auto: "Automatisk",
	day: "Dag",
	week: "Vecka",
	month: "Månad",
	quarter: "Kvartal",
	year: "År",
};

/**
 * Report-level dimension filters, bound into :filter(...) on the backend. Each
 * list is "any of". Koncern and dotterbolag are the customer's, so they apply
 * through the customer of each row (never the consultant's subsidiary).
 */
export interface ReportFilters {
	corporateGroupIds?: string[];
	poolIds?: string[];
	/** Subsidiary ids; NO_SUBSIDIARY = customers connected to the organisation directly. */
	subsidiaryIds?: string[];
	contractIds?: string[];
	/** Legacy: reports saved before koncern/dotterbolag replaced these. Still applied and clearable. */
	customerIds?: string[];
	consultantIds?: string[];
}

export interface FilterDimension {
	key: keyof ReportFilters;
	label: string;
	labelOne: string;
	/** Where the options come from: the lake, or the organisation's koncern/dotterbolag lists. */
	source: "lake" | "corporateGroups" | "subsidiaries";
	/** Only shown while a saved report still has values for it. */
	legacy?: boolean;
}

export const FILTER_DIMENSIONS: FilterDimension[] = [
	{ key: "corporateGroupIds", label: "Koncern", labelOne: "koncern", source: "corporateGroups" },
	{ key: "poolIds", label: "Pooler", labelOne: "pool", source: "lake" },
	{ key: "subsidiaryIds", label: "Dotterbolag", labelOne: "dotterbolag", source: "subsidiaries" },
	{ key: "contractIds", label: "Avtal", labelOne: "avtal", source: "lake" },
	{ key: "customerIds", label: "Kunder", labelOne: "kund", source: "lake", legacy: true },
	{ key: "consultantIds", label: "Konsulter", labelOne: "konsult", source: "lake", legacy: true },
];

export interface NumberFormatOptions {
	/** Fixed decimals; absent = automatic (0 for integers, 1 for small fractions). */
	decimals?: number;
	/** 1,2 mkr / 3,4 tkr style. */
	compact?: boolean;
	prefix?: string;
	/** Replaces the unit after the number when set. */
	suffix?: string;
}

/** Colour rule: the first threshold whose `from` the value reaches (highest first) wins. */
export interface Threshold {
	from: number;
	color: "green" | "amber" | "red" | "gray";
}

export interface ReferenceLine {
	value: number;
	label?: string;
	color?: string;
}

export type WidgetCompareSetting = "report" | "none" | ComparisonMode;

/**
 * Presentation choices made in the builder. Lives beside the spec so a
 * regenerate never loses them; everything here is applied client-side.
 */
export interface WidgetDisplay {
	/** Manual title/subtitle/unit; the generated text remains the placeholder. */
	title?: string;
	description?: string;
	unit?: string;
	format?: NumberFormatOptions;
	thresholds?: Threshold[];
	/** Cell colouring in tables uses the same thresholds on these columns (all numeric when absent). */
	thresholdColumns?: string[];
	sort?: { key: string; direction: "asc" | "desc" };
	/** Keep the first N rows after sorting; `rest` folds the remainder into an "Övriga" row. */
	limit?: { n: number; rest?: boolean };
	axis?: {
		yFromZero?: boolean;
		log?: boolean;
		hideLegend?: boolean;
		rotateLabels?: boolean;
		/** Series colours by key. */
		colors?: Record<string, string>;
		/** Horizontal average line over the first series. */
		average?: boolean;
	};
	referenceLines?: ReferenceLine[];
	/** Per-widget comparison; absent or "report" = follow the report. */
	compare?: WidgetCompareSetting;
	/** Table columns: hidden ones and the display order. */
	columns?: { hidden?: string[]; order?: string[] };
}

export interface WidgetHistoryEntry {
	prompt: string;
	spec: WidgetSpec;
	at: string;
}

export type ComparisonMode = "previous_period" | "same_period_last_year";

export const COMPARISON_OPTIONS: {
	value: ComparisonMode | "none";
	label: string;
	short: string;
}[] = [
	{ value: "none", label: "Ingen jämförelse", short: "" },
	{ value: "previous_period", label: "Mot föregående period", short: "föreg. period" },
	{ value: "same_period_last_year", label: "Mot samma period förra året", short: "förra året" },
];

export interface LakeQueryRequest {
	sql: string;
	from?: string;
	to?: string;
	maxRows?: number;
	compare?: ComparisonMode;
	bucket?: BucketSetting;
	filters?: ReportFilters;
}

export interface LakeComparisonResult {
	mode: ComparisonMode;
	range: { from: string; to: string };
	rows: Record<string, unknown>[];
	rowCount: number;
	truncated: boolean;
}

export interface LakeQueryResponse {
	dataAsOf?: string;
	columns: string[];
	rows: Record<string, unknown>[];
	rowCount: number;
	truncated: boolean;
	sql: string;
	range: { from: string; to: string };
	comparison?: LakeComparisonResult;
	/** The grain :bucket(col) was bound to; absent when the SQL has no bucket placeholder. */
	grain?: Grain;
}

export interface GenerateWidgetRequest {
	prompt: string;
	current?: WidgetSpec;
	from?: string;
	to?: string;
	compare?: ComparisonMode;
	bucket?: BucketSetting;
	filters?: ReportFilters;
}

export interface GenerateWidgetResponse {
	spec: WidgetSpec;
	preview?: LakeQueryResponse;
	previewError?: string;
}

/** Legacy size (store v1); migrated on load. */
export type WidgetSize = "S" | "M" | "L";

/** Legacy grid position (store v2/v3 and shares saved before rows); only read when resetting to rows. */
export interface WidgetLayout {
	x: number;
	y: number;
	w: number;
	h: number;
}

/**
 * A row of the report: up to MAX_PER_ROW widgets sharing the width equally.
 * The dashboard's layout is nothing but the ordered rows and the order of
 * widget ids inside each; there are no coordinates or sizes to keep in sync.
 */
export interface ReportRow {
	id: string;
	widgets: string[];
}

export const MAX_PER_ROW = 4;

/** Widget height in px by chart type; a row is as tall as its tallest widget. */
export const widgetHeight = (widget: { spec: Pick<WidgetSpec, "chart">; note?: string }): number =>
	typeof widget.note === "string"
		? 152
		: widget.spec.chart.type === "number"
		? 208
		: widget.spec.chart.type === "table"
		? 488
		: 432;

/** A widget placed on a report: the spec and the prompt that made it. Its place is given by the dashboard's rows. */
export interface ReportWidget {
	id: string;
	spec: WidgetSpec;
	prompt: string;
	created: string;
	display?: WidgetDisplay;
	/** Markdown text widget; no query runs. The spec is NOTE_SPEC then. */
	note?: string;
	/** Earlier generations, newest last; "Historik" reverts to one. */
	history?: WidgetHistoryEntry[];
}

/** Placeholder spec of a note widget, so shares and validation see a harmless query. */
export const NOTE_SPEC: WidgetSpec = {
	title: "Anteckning",
	sql: "select 1 as x",
	chart: { type: "number", yKeys: ["x"] },
	usesDateRange: false,
};

export const isNote = (widget: { note?: string }): boolean => typeof widget.note === "string";

/** The title and subtitle to show: manual overrides first, the generated text as fallback. */
export const widgetTitle = (widget: {
	spec: { title: string; description?: string };
	display?: WidgetDisplay;
}) => ({
	title: widget.display?.title?.trim() || widget.spec.title,
	description: widget.display?.description?.trim() ?? widget.spec.description,
});

export interface ReportShareInfo {
	shareId: string;
	path: string;
	hasPin: boolean;
	updatedAt: string;
}

/** What the server stores for a public share. */
export interface SharedWidget {
	id: string;
	spec: WidgetSpec;
	display?: WidgetDisplay;
	note?: string;
}

export interface SharedDashboard {
	name: string;
	range: ReportDateRange;
	compare?: ComparisonMode;
	filters?: ReportFilters;
	rows: ReportRow[];
	widgets: SharedWidget[];
}

export interface PublicDashboard {
	shareId: string;
	name: string;
	range: ReportDateRange;
	compare?: ComparisonMode;
	filters?: ReportFilters;
	/** Missing on shares saved before rows; those are reset to one widget per row. */
	rows?: ReportRow[];
	widgets: {
		id: string;
		spec: Omit<WidgetSpec, "sql">;
		/** Legacy grid position on shares saved before rows. */
		layout?: WidgetLayout;
		display?: WidgetDisplay;
		note?: string;
	}[];
	updatedAt: string;
}

export type DateRangePreset =
	| "today"
	| "yesterday"
	| "this_week"
	| "last_week"
	| "this_month"
	| "last_month"
	| "this_quarter"
	| "last_quarter"
	| "this_year"
	| "last_year"
	| "year_to_date"
	| "last_7_days"
	| "last_30_days"
	| "last_90_days"
	| "last_12_months"
	| "custom";

export interface ReportDateRange {
	preset: DateRangePreset;
	/** YYYY-MM-DD, used when preset = custom. */
	from?: string;
	to?: string;
}

export interface ReportDashboard {
	id: string;
	name: string;
	range: ReportDateRange;
	/** Period comparison applied to every widget that follows the date range. */
	compare?: ComparisonMode;
	filters?: ReportFilters;
	widgets: ReportWidget[];
	/** Layout: ordered rows of widget ids; every widget appears in exactly one row. */
	rows: ReportRow[];
	created: string;
	/** Present while the report is published at a public link. */
	share?: ReportShareInfo;
	/** Internal view refetch interval in minutes; absent = manual only. */
	autoRefreshMinutes?: number;
	/** Who reaches it inside the app. Absent on dashboards saved before server storage = private. */
	visibility?: DashboardVisibility;
	/**
	 * The colleagues it is shared with by name. Kept while visibility is not
	 * `users`, so the list survives switching access off and on again.
	 */
	sharedWith?: DashboardRecipient[];
	/** Set on a colleague's dashboard opened read-only; absent on one's own. */
	owner?: DashboardOwner;
}

/**
 * `private`: the owner only. `users`: the people named in `sharedWith`.
 * `organisation`: every dashboard user in the organisation. All read-only.
 * A public link is separate from all three — see `share`.
 */
export type DashboardVisibility = "private" | "users" | "organisation";

/** One internal user a dashboard is shared with; the name is stored so the list renders without a lookup. */
export interface DashboardRecipient {
	id: string;
	name?: string;
}

export interface DashboardOwner {
	id: string;
	name?: string;
}

/** A colleague's dashboard as listed in the menu, before it is opened. */
export interface SharedDashboardSummary {
	id: string;
	name: string;
	ownerId: string;
	ownerName?: string;
	updatedAt: string;
}

/** One dashboard as the server keeps it: the document plus who owns and sees it. */
export interface StoredDashboard {
	id: string;
	name: string;
	visibility: DashboardVisibility;
	/** Only sent to the owner; a recipient never learns who else has it. */
	sharedWith?: DashboardRecipient[];
	ownerId: string;
	ownerName?: string;
	updatedAt: string;
	dashboard: ReportDashboard;
}

export interface StoredDashboardsResponse {
	own: StoredDashboard[];
	shared: SharedDashboardSummary[];
}

export interface ExplainWidgetResponse {
	explanation: string;
}

export interface ReportPlan {
	name: string;
	period: WidgetPeriod;
	compare?: ComparisonMode;
	widgets: { prompt: string; size: "S" | "M" | "L" }[];
}

/** Dimension options for the filter bar, read from the lake itself. */
export interface FilterOption {
	id: string;
	name: string;
}
